import type { ChartLink, ChartSpec } from "@/types";
import type { EmissionsSankeyItem } from "../types/dashboard";
import { getDashboardColors, readThemeColors } from "@/components/charts/theme";

/** Shared projection for the built-in chart and saved live hierarchies. */
export function emissionSankeyData(
  scopes: EmissionsSankeyItem[],
  unit: "kgCO2e" | "tCO2e",
  isDark: boolean,
): ChartSpec["data"] {
  const colors = getDashboardColors(isDark);
  const factor = unit === "tCO2e" ? 1 / 1000 : 1;
  const nodes = new Set<string>();
  const links: ChartLink[] = [];
  const nodeColors: Record<string, string> = {};
  const byCode = (a: { code: string }, b: { code: string }) =>
    a.code.localeCompare(b.code, undefined, { numeric: true });
  for (const scope of [...scopes].sort(byCode)) {
    nodes.add(scope.code);
    const scopeColor =
      colors.scope[Number(scope.code.match(/\d+/)?.[0])] ??
      readThemeColors(isDark).theme.secondary;
    nodeColors[scope.code] = scopeColor;
    for (const category of [...(scope.categories ?? [])].sort(byCode)) {
      nodes.add(category.code);
      // GHG has a neutral Total root, followed by the actual scopes.
      const color =
        scope.code === "Total"
          ? (colors.scope[Number(category.code.match(/\d+/)?.[0])] ??
            scopeColor)
          : scopeColor;
      nodeColors[category.code] = color;
      links.push({
        source: scope.code,
        target: category.code,
        value: category.value * factor,
      });
      for (const leaf of [...(category.subcategories ?? [])].sort(byCode)) {
        nodes.add(leaf.code);
        nodeColors[leaf.code] = color;
        links.push({
          source: category.code,
          target: leaf.code,
          value: leaf.value * factor,
        });
      }
    }
  }
  return {
    labels: [...nodes],
    datasets: [],
    links,
    nodeColors,
    unit,
    respectUnit: true,
    sankeyLineOpacity: isDark ? 0.3 : 0.32,
  };
}
